// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Box, Button, Group, Loader, Modal, Paper, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { MedicationRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { IconDownload, IconEye, IconPlus, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { NewPrescriptionModal } from '../../../components/encounter/NewPrescriptionModal';
import type { PatientDocument } from '../../../hooks/usePatientDocuments';
import { useRole } from '../../../hooks/useRole';
import { hasPermission } from '../../../utils/permissions';
import {
  assertTransactionSucceeded,
  buildPrescriptionDeletionTransaction,
  findCurrentPrescriptionArtifacts,
  invalidateOrderTransactionCaches,
} from '../../../utils/prescription-pdf';
import { isReferralMedicationRequest } from '../../../utils/referral-pdf';

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MM/DD/YYYY');
}

function getMedicationText(request: MedicationRequest): string {
  return (
    request.medicationCodeableConcept?.text ??
    request.medicationCodeableConcept?.coding?.[0]?.display ??
    request.medicationCodeableConcept?.coding?.[0]?.code ??
    '—'
  );
}

function isDmePrescription(request: MedicationRequest): boolean {
  return (
    request.medicationCodeableConcept?.coding?.some(
      (coding) => coding.system === 'http://polarissleep.com/therapies'
    ) ?? false
  );
}

export interface PrescriptionsSectionProps {
  prescriptions: MedicationRequest[];
  loading: boolean;
  patient: import('@medplum/fhirtypes').Patient;
  onRefresh: () => void;
  documents: PatientDocument[];
}

export function PrescriptionsSection({
  prescriptions,
  loading,
  patient,
  onRefresh,
  documents,
}: PrescriptionsSectionProps): JSX.Element {
  const { role, isAdmin } = useRole();
  const canOrder = hasPermission({ role, permission: 'prescribe-cpap', isAdmin });
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [prescriptionToDelete, setPrescriptionToDelete] = useState<MedicationRequest>();
  const [deleting, setDeleting] = useState(false);
  const [orderPickerOpened, setOrderPickerOpened] = useState(false);
  const [dmeOrderOpened, setDmeOrderOpened] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string>();
  const [documentTitle, setDocumentTitle] = useState('Prescription');

  const getDocument = (request: MedicationRequest): PatientDocument | undefined =>
    documents.find(
      (document) =>
        document.documentReference?.status === 'current' &&
        document.documentReference?.context?.related?.some(
          (reference) =>
            reference.reference === `MedicationRequest/${request.id}` ||
            reference.reference?.endsWith(`/MedicationRequest/${request.id}`)
        )
    );

  const handleViewDocument = async (request: MedicationRequest): Promise<void> => {
    const document = getDocument(request);
    if (!document?.url) {
      return;
    }
    const url = document.url.startsWith('Binary/')
      ? URL.createObjectURL(await medplum.download(document.url))
      : document.url;
    setDocumentTitle(document.title);
    setDocumentUrl(url);
  };

  useEffect(() => {
    return () => {
      if (documentUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentUrl]);

  const handleDelete = async (): Promise<void> => {
    if (!prescriptionToDelete?.id) {
      return;
    }
    setDeleting(true);
    try {
      // Retire the order's current PDF and revoke its vendor ServiceRequest in the same transaction as the delete.
      const artifacts = await findCurrentPrescriptionArtifacts(medplum, prescriptionToDelete.id);
      const transaction = buildPrescriptionDeletionTransaction(artifacts, prescriptionToDelete.id);
      const response = await medplum.executeBatch(transaction);
      invalidateOrderTransactionCaches(medplum, transaction, response);
      assertTransactionSucceeded(response);
      notifications.show({ color: 'green', message: 'Prescription deleted' });
      setPrescriptionToDelete(undefined);
      onRefresh();
    } catch (error) {
      Sentry.captureException(error);
      notifications.show({ color: 'red', message: normalizeErrorString(error) });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Prescriptions</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          disabled={!canOrder}
          title={canOrder ? 'Choose an order type' : 'You do not have permission to prescribe'}
          onClick={() => setOrderPickerOpened(true)}
        >
          Order
        </Button>
      </Group>

      {(() => {
        if (loading) {
          return (
            <Stack align="center" py="md">
              <Loader size="sm" />
            </Stack>
          );
        }
        if (prescriptions.length === 0) {
          return (
            <Text c="dimmed" ta="center" py="md">
              No prescriptions on file.
            </Text>
          );
        }
        return (
          <Box style={{ overflowX: 'auto' }}>
            <Table highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Medication</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Authored</Table.Th>
                  <Table.Th>Prescriber</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {prescriptions.map((request) => (
                  <Table.Tr key={request.id}>
                    <Table.Td>{getMedicationText(request)}</Table.Td>
                    <Table.Td>{request.status ?? '—'}</Table.Td>
                    <Table.Td>{formatDate(request.authoredOn)}</Table.Td>
                    <Table.Td>{request.requester?.display ?? '—'}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          leftSection={<IconEye size={14} />}
                          onClick={() =>
                            navigate(
                              isDmePrescription(request)
                                ? `/DmeOrder/${patient.id}?prescriptionId=${request.id ?? ''}${
                                    isReferralMedicationRequest(request) ? '&type=referral' : ''
                                  }`
                                : `/RpmOrder/${patient.id}?prescriptionId=${request.id ?? ''}`
                            )
                          }
                        >
                          View
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => setPrescriptionToDelete(request)}
                        >
                          Delete
                        </Button>
                        {getDocument(request) && (
                          <Button size="compact-xs" variant="subtle" onClick={() => handleViewDocument(request)}>
                            View PDF
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        );
      })()}

      <Modal
        opened={Boolean(prescriptionToDelete)}
        onClose={() => setPrescriptionToDelete(undefined)}
        title="Delete Prescription"
        centered
      >
        <Stack>
          <Text>Delete this prescription? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPrescriptionToDelete(undefined)}>
              Cancel
            </Button>
            <Button color="red" loading={deleting} onClick={handleDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={Boolean(documentUrl)} onClose={() => setDocumentUrl(undefined)} title={documentTitle} size="xl">
        {documentUrl && (
          <Stack>
            <iframe title={documentTitle} src={documentUrl} style={{ width: '100%', height: '70vh', border: 0 }} />
            <Anchor href={documentUrl} download={`${documentTitle}.pdf`}>
              <IconDownload size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Download PDF
            </Anchor>
          </Stack>
        )}
      </Modal>
      <Modal opened={orderPickerOpened} onClose={() => setOrderPickerOpened(false)} title="New order" centered>
        <Stack>
          <Text c="dimmed">Choose the order form to open.</Text>
          <SimpleGrid cols={2}>
            <Button
              variant="light"
              onClick={() => {
                setOrderPickerOpened(false);
                navigate(`/RpmOrder/${patient.id}`);
              }}
            >
              RPM Order Form
            </Button>
            <Button
              variant="light"
              onClick={() => {
                setOrderPickerOpened(false);
                setDmeOrderOpened(true);
              }}
            >
              DME Order
            </Button>
          </SimpleGrid>
        </Stack>
      </Modal>
      <NewPrescriptionModal
        opened={dmeOrderOpened}
        onClose={() => setDmeOrderOpened(false)}
        patient={patient}
        onOrdered={() => onRefresh()}
      />
    </Paper>
  );
}
